import API_BASE_URL from './config';
import React, { useState, useEffect } from 'react';

function AdminKennels() {
  const [pendingKennels, setPendingKennels] = useState([]);
  const [deniedKennels, setDeniedKennels] = useState([]);
  const [message, setMessage] = useState('');
  const [kennelImages, setKennelImages] = useState({});
  const [denyReasons, setDenyReasons] = useState({});
  const [replies, setReplies] = useState({});
  const [loading, setLoading] = useState(true);
  const adminId = localStorage.getItem('playerId');

  useEffect(() => {
    if (!adminId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const pendingRes = await fetch(`${API_BASE_URL}/admin-pending?adminId=${adminId}`);
        if (!pendingRes.ok) throw new Error(await pendingRes.text());
        const pendingData = await pendingRes.json();
        setPendingKennels(pendingData.kennels || []);

        const deniedRes = await fetch(`${API_BASE_URL}/admin-denied-kennels?adminId=${adminId}`);
        if (!deniedRes.ok) throw new Error(await deniedRes.text());
        setDeniedKennels(await deniedRes.json() || []);
      } catch (error) {
        setMessage('Error loading kennels: ' + error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [adminId]);

  const approveKennel = async (kennelId) => {
    try {
      const kennel = pendingKennels.find(k => k._id === kennelId);
      if (kennel.playerInfo._id === adminId) {
        setMessage('Admins cannot approve their own kennels.');
        return;
      }
      const artUrl = kennelImages[kennelId];
      const res = await fetch(`${API_BASE_URL}/admin-approve-kennel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kennelId, artUrl })
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMessage(data.message);
      setPendingKennels(pendingKennels.filter(k => k._id !== kennelId));
      setKennelImages(prev => { const { [kennelId]: _, ...rest } = prev; return rest; });
    } catch (error) {
      setMessage('Error approving kennel: ' + error.message);
    }
  };

  const denyKennel = async (kennelId) => {
    try {
      const denyReason = denyReasons[kennelId] || 'No reason provided';
      const res = await fetch(`${API_BASE_URL}/admin-deny-kennel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kennelId, denyReason })
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMessage(data.message);
      setPendingKennels(pendingKennels.filter(k => k._id !== kennelId));
      setDenyReasons(prev => { const { [kennelId]: _, ...rest } = prev; return rest; });
    } catch (error) {
      setMessage('Error denying kennel: ' + error.message);
    }
  };

  const clearDeniedKennel = async (kennelId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/kennel/${kennelId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error(await res.text());
      setDeniedKennels(deniedKennels.filter(k => k._id !== kennelId));
      setMessage('Kennel cleared!');
    } catch (error) {
      setMessage('Error clearing kennel: ' + error.message);
    }
  };

  const sendKennelReply = async (kennelId) => {
    try {
      const text = replies[kennelId] || '';
      if (!text) return;
      const res = await fetch(`${API_BASE_URL}/kennel-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kennelId, senderId: adminId, senderRole: 'admin', text })
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMessage(data.message);
      setPendingKennels(pendingKennels.map(k => k._id === kennelId ? { ...k, messages: [...k.messages, { senderId: adminId, senderRole: 'admin', text, timestamp: new Date(), unread: true }] } : k));
      setReplies(prev => ({ ...prev, [kennelId]: '' }));
    } catch (error) {
      setMessage('Error sending reply: ' + error.message);
    }
  };

  const markKennelMessageRead = async (kennelId, msgIdx) => {
    try {
      const kennel = pendingKennels.find(k => k._id === kennelId);
      const updatedMessages = kennel.messages.map((m, i) => i === msgIdx ? { ...m, unread: false } : m);
      const res = await fetch(`${API_BASE_URL}/kennel/${kennelId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...kennel, messages: updatedMessages })
      });
      if (!res.ok) throw new Error(await res.text());
      setPendingKennels(pendingKennels.map(k => k._id === kennelId ? { ...k, messages: updatedMessages } : k));
    } catch (error) {
      setMessage('Error marking message as read: ' + error.message);
    }
  };

  if (!adminId) return <p style={{ textAlign: 'center', color: '#333' }}>Please log in as an admin.</p>;
  if (loading) return <p style={{ textAlign: 'center', color: '#333' }}>Loading kennel approvals...</p>;

  return (
    <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      <h1 style={{ textAlign: 'center', color: '#333', marginBottom: '20px' }}>Kennel Approvals</h1>
      {message && <p style={{ textAlign: 'center', color: message.includes('Error') ? '#FF4444' : '#4CAF50' }}>{message}</p>}

      <h2 style={{ color: '#4CAF50', marginBottom: '15px' }}>Pending Kennels</h2>
      {pendingKennels.length > 0 ? (
        pendingKennels.map(kennel => (
          <div key={kennel._id} style={{ marginBottom: '20px', padding: '15px', background: '#f9f9f9', borderRadius: '5px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
            <p><strong>{kennel.kennelName}</strong></p>
            <p>Owner: {kennel.playerInfo?.username || 'Unknown'} (ID: {kennel.playerId})</p>
            <p>Submitted Artwork: <a href={kennel.stashLink} target="_blank" rel="noopener noreferrer" style={{ color: '#4CAF50' }}>{kennel.stashLink || 'N/A'}</a></p>
            <label>Image URL: <input value={kennelImages[kennel._id] || ''} onChange={e => setKennelImages({ ...kennelImages, [kennel._id]: e.target.value })} style={{ width: '100%', padding: '5px', marginTop: '5px' }} /></label>
            <div style={{ marginTop: '10px' }}>
              <button onClick={() => approveKennel(kennel._id)} style={{ background: '#4CAF50', color: 'white', padding: '8px 15px', border: 'none', borderRadius: '3px', marginRight: '10px' }}>Approve</button>
              <button onClick={() => denyKennel(kennel._id)} style={{ background: '#FF69B4', color: 'white', padding: '8px 15px', border: 'none', borderRadius: '3px', marginRight: '10px' }}>Deny</button>
              <input value={denyReasons[kennel._id] || ''} onChange={e => setDenyReasons({ ...denyReasons, [kennel._id]: e.target.value })} placeholder="Reason for denial" style={{ padding: '5px', width: '200px' }} />
            </div>
            <h3 style={{ color: '#4CAF50', marginTop: '10px' }}>Messages</h3>
            {kennel.messages?.length > 0 ? (
              <ul style={{ paddingLeft: '20px' }}>
                {kennel.messages.map((msg, idx) => (
                  <li key={idx} style={{ color: msg.unread && msg.senderRole === 'player' ? '#FF4444' : '#333' }}>
                    {msg.senderRole}: {msg.text} ({new Date(msg.timestamp).toLocaleString()})
                    {msg.unread && msg.senderRole === 'player' && (
                      <button onClick={() => markKennelMessageRead(kennel._id, idx)} style={{ marginLeft: '10px', background: '#4CAF50', color: 'white', border: 'none', padding: '2px 5px', borderRadius: '3px' }}>Mark Read</button>
                    )}
                  </li>
                ))}
              </ul>
            ) : <p>No messages.</p>}
            <textarea value={replies[kennel._id] || ''} onChange={e => setReplies({ ...replies, [kennel._id]: e.target.value })} placeholder="Reply..." style={{ width: '100%', marginTop: '10px', padding: '5px' }} />
            <button onClick={() => sendKennelReply(kennel._id)} style={{ background: '#4CAF50', color: 'white', padding: '8px 15px', border: 'none', borderRadius: '3px', marginTop: '5px' }}>Send Reply</button>
          </div>
        ))
      ) : <p style={{ textAlign: 'center', color: '#888' }}>No pending kennels.</p>}

      <h2 style={{ color: '#4CAF50', marginBottom: '15px', marginTop: '20px' }}>Denied Kennels</h2>
      {deniedKennels.length > 0 ? (
        deniedKennels.map(kennel => (
          <div key={kennel._id} style={{ marginBottom: '20px', padding: '15px', background: '#f9f9f9', borderRadius: '5px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
            <p><strong>{kennel.kennelName}</strong> - Denied</p>
            <p>Owner: {kennel.playerInfo?.username || 'Unknown'} (ID: {kennel.playerId})</p>
            <p>Submitted Artwork: <a href={kennel.stashLink} target="_blank" rel="noopener noreferrer" style={{ color: '#4CAF50' }}>{kennel.stashLink || 'N/A'}</a></p>
            <p>Reason: {kennel.denyReason || 'N/A'}</p>
            <button onClick={() => clearDeniedKennel(kennel._id)} style={{ background: '#FF69B4', color: 'white', padding: '8px 15px', border: 'none', borderRadius: '3px', marginTop: '10px' }}>Clear</button>
            <h3 style={{ color: '#4CAF50', marginTop: '10px' }}>Messages</h3>
            {kennel.messages?.length > 0 ? (
              <ul style={{ paddingLeft: '20px' }}>
                {kennel.messages.map((msg, idx) => (
                  <li key={idx} style={{ color: msg.unread && msg.senderRole === 'player' ? '#FF4444' : '#333' }}>
                    {msg.senderRole}: {msg.text} ({new Date(msg.timestamp).toLocaleString()})
                  </li>
                ))}
              </ul>
            ) : <p>No messages.</p>}
            <textarea value={replies[kennel._id] || ''} onChange={e => setReplies({ ...replies, [kennel._id]: e.target.value })} placeholder="Reply..." style={{ width: '100%', marginTop: '10px', padding: '5px' }} />
            <button onClick={() => sendKennelReply(kennel._id)} style={{ background: '#4CAF50', color: 'white', padding: '8px 15px', border: 'none', borderRadius: '3px', marginTop: '5px' }}>Send Reply</button>
          </div>
        ))
      ) : <p style={{ textAlign: 'center', color: '#888' }}>No denied kennels.</p>}
    </div>
  );
}

export default AdminKennels;